import React, { useEffect, useState } from 'react';
import { Table, Tag, Alert, Button, Space, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { MembroCPPD } from '../../../../types/dashboard/cppd.types';

interface CPPDMembrosTableProps {
  data: MembroCPPD[];
  loading: boolean;
  searchText?: string;
  error?: string | null;
  onRetry?: () => void;
  onEdit?: (membro: MembroCPPD) => void;
  onDelete?: (id: number | string) => void;
  onPaginationChange?: (page: number, pageSize?: number) => void;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
}

export const CPPDMembrosTable: React.FC<CPPDMembrosTableProps> = ({
  data,
  loading,
  searchText = '',
  error = null,
  onRetry,
  onEdit,
  onDelete,
  onPaginationChange,
  pagination
}) => {
  // Estado local para dados filtrados
  const [filteredData, setFilteredData] = useState<MembroCPPD[]>(data);

  // Efeito para filtrar dados quando searchText mudar
  useEffect(() => {
    if (!searchText) {
      setFilteredData(data);
      return;
    }

    const lowerCaseSearch = searchText.toLowerCase();
    const filtered = data.filter(membro => 
      (membro.nome && membro.nome.toLowerCase().includes(lowerCaseSearch)) ||
      (membro.email && membro.email.toLowerCase().includes(lowerCaseSearch)) ||
      (membro.funcao && membro.funcao.toLowerCase().includes(lowerCaseSearch))
    );
    setFilteredData(filtered);
  }, [data, searchText]);

  // Função para obter um ID válido do registro
  const getValidId = (record: MembroCPPD): string | number => {
    if (record.id !== undefined && record.id !== null) {
      return record.id;
    }
    return record.nome || record.email || `membro-${Math.random()}`;
  };

  // Colunas para a tabela de membros
  const baseColumns: ColumnsType<MembroCPPD> = [
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      sorter: (a: MembroCPPD, b: MembroCPPD) => (a.nome || '').localeCompare(b.nome || ''),
      render: (text) => <span style={{ fontWeight: 500, color: '#101828' }}>{text || 'Sem nome'}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => <span style={{ color: '#667085' }}>{text || '-'}</span>,
    },
    {
      title: 'Função',
      dataIndex: 'funcao',
      key: 'funcao',
      render: (funcao) => {
        if (!funcao) {
          return (
            <Tag style={{ 
              borderRadius: '16px', 
              backgroundColor: '#F2F4F7',
              color: '#667085',
              border: 'none',
              padding: '2px 8px',
              fontSize: '12px'
            }}>
              Sem função definida
            </Tag>
          );
        }

        // Definir cores baseadas na função
        let backgroundColor = '#F2F4F7';
        let color = '#344054';
        
        if (funcao === 'Presidente') {
          backgroundColor = '#FEF3F2';
          color = '#D92D20';
        } else if (funcao === 'Secretário') {
          backgroundColor = '#EFF8FF';
          color = '#175CD3';
        } else if (funcao === 'Membro Efetivo') {
          backgroundColor = '#ECFDF3';
          color = '#16B364';
        } else if (funcao === 'Membro Suplente') {
          backgroundColor = '#FFF6ED';
          color = '#F79009';
        }
        
        return (
          <Tag 
            style={{ 
              marginRight: 4, 
              marginBottom: 4,
              borderRadius: '16px', 
              backgroundColor,
              color,
              border: 'none',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: 500
            }}
          >
            {funcao}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusValue = status || 'Ativo';
        let color = '';
        let dotColor = '';
        
        if (statusValue === 'Ativo') {
          color = '#ECFDF3';
          dotColor = '#16B364';
        } else if (statusValue === 'Pendente') {
          color = '#FFF6ED';
          dotColor = '#F79009';
        } else {
          color = '#F2F4F7';
          dotColor = '#667085';
        }
        
        return (
          <Tag
            style={{
              backgroundColor: color,
              borderRadius: '16px',
              color: statusValue === 'Ativo' ? '#16B364' : statusValue === 'Pendente' ? '#F79009' : '#667085',
              border: 'none',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span 
              style={{ 
                display: 'inline-block', 
                width: '6px', 
                height: '6px', 
                borderRadius: '50%', 
                backgroundColor: dotColor,
                marginRight: '4px'
              }}
            />
            {statusValue}
          </Tag>
        );
      },
      filters: [
        { text: 'Ativo', value: 'Ativo' },
        { text: 'Pendente', value: 'Pendente' },
        { text: 'Inativo', value: 'Inativo' },
      ],
      onFilter: (value: string | number | boolean, record: MembroCPPD) => 
        (record.status || 'Ativo') === value.toString(),
    },
    {
      title: 'Participação',
      key: 'participacao',
      render: (_, record) => {
        if (record.taxaParticipacao !== undefined) {
          const taxa = record.taxaParticipacao;
          let color = '#667085';
          
          if (taxa >= 80) color = '#16B364';
          else if (taxa >= 60) color = '#F79009';
          else if (taxa >= 40) color = '#F04438';
          
          return (
            <div style={{ textAlign: 'center' }}>
              <div style={{ color, fontWeight: 500, fontSize: '14px' }}>
                {taxa.toFixed(1)}%
              </div>
              <div style={{ color: '#667085', fontSize: '12px' }}>
                {record.presente || 0}/{record.convidado || 0}
              </div>
            </div>
          );
        }
        
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#667085', fontSize: '14px' }}>
              -
            </div>
            <div style={{ color: '#667085', fontSize: '12px' }}>
              sem dados
            </div>
          </div>
        );
      },
      sorter: (a: MembroCPPD, b: MembroCPPD) => 
        (a.taxaParticipacao || 0) - (b.taxaParticipacao || 0),
      align: 'center',
    }
  ];
  
  // Adicionar coluna de ações se houver funções onEdit ou onDelete
  const columns = [...baseColumns];
  
  if (onEdit || onDelete) {
    columns.push({
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {onEdit && (
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => onEdit(record)} 
              title="Editar"
            />
          )}
          {onDelete && (
            <Popconfirm
              title="Tem certeza que deseja excluir este membro?"
              onConfirm={() => onDelete(getValidId(record))}
              okText="Sim"
              cancelText="Não"
            >
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                title="Excluir"
              />
            </Popconfirm>
          )}
        </Space>
      ),
      width: 100,
      align: 'center',
    });
  }

  // Mostrar alerta de erro se houver
  if (error) {
    return (
      <>
        <Alert
          message="Erro ao carregar membros"
          description={error}
          type="error"
          showIcon
          action={
            onRetry && (
              <Button size="small" onClick={onRetry}>
                Tentar novamente
              </Button>
            )
          }
          style={{ marginBottom: 16 }}
        />
        <Table
          dataSource={[]}
          columns={columns}
          loading={loading}
          locale={{ emptyText: 'Nenhum membro disponível' }}
        />
      </>
    );
  }

  return (
    <Table
      dataSource={filteredData}
      columns={columns}
      rowKey={(record) => getValidId(record)}
      pagination={onPaginationChange ? {
        current: pagination?.current || 1,
        pageSize: pagination?.pageSize || 10,
        total: pagination?.total || filteredData.length,
        onChange: onPaginationChange,
        showSizeChanger: true,
        showTotal: (total) => `Total de ${total} membros`,
        pageSizeOptions: ['10', '20', '50', '100'],
        showQuickJumper: true,
      } : false}
      loading={loading}
      locale={{ emptyText: 'Nenhum membro encontrado' }}
      style={{ 
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)', 
        borderRadius: '8px',
        overflow: 'hidden'
      }}
      scroll={{ x: 800 }}
    />
  );
};

export default CPPDMembrosTable;